import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { useShallow } from "zustand/react/shallow";

import { experienceService } from "@/services/experienceService";
import useTripStore from "@/stores/tripBuilder/useTrip";
import useCurrencyStore from "@/stores/ui/currencyStore";
import useCustomActivityStore from "@/stores/tripBuilder/useCustomActivity";
import { ProgressBar } from "@tripbuilder/common/ProgressBar";
import { AIService } from "@/services/chatService";
import PreviewCard from "./PreviewCard";
import { LocationInput, FormData } from "@/types/custom";
import { questions } from "./question";
import { useHandleNext } from "./navigationUtils";

// Import the modular step components
import {
  TitleStep,
  DescriptionStep,
  DurationCostStep,
  TypesStep,
  LocationStep,
  ImageStep,
  SharingStep,
  NavigationButtons,
} from "./steps";

// Memoized question title component
const QuestionTitle = React.memo(
  ({ question }: { question: string | undefined }) => (
    <h2 className="text-md font-medium text-neutral-800">{question}</h2>
  )
);

// Memoized preview section header
const PreviewHeader = React.memo(() => (
  <div className="flex items-center gap-2 mb-3">
    <p className="text-base rounded-md px-2 py-1 bg-indigo-50 text-indigo-700 font-medium">
      Preview
    </p>
    <span className="text-xs text-neutral-500 2xl:text-sm">
      Review your activity details before adding it to your trip
    </span>
  </div>
));

// Individual step wrappers with direct store access
const TitleStepWrapper = React.memo(() => {
  const title = useCustomActivityStore((state) => state.title);
  const setTitle = useCustomActivityStore((state) => state.setTitle);
  const currentStep = useCustomActivityStore((state) => state.currentStepIndex);
  const setCurrentStep = useCustomActivityStore(
    (state) => state.setCurrentStepIndex
  );
  const { handleNext } = useHandleNext();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleNext(currentStep, setCurrentStep, questions.length);
    }
  };

  return (
    <TitleStep
      value={title}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
    />
  );
});

const DescriptionStepWrapper = React.memo(() => {
  const { toast } = useToast();
  const title = useCustomActivityStore((state) => state.title);
  const description = useCustomActivityStore((state) => state.description);
  const isGeneratingDescription = useCustomActivityStore(
    (state) => state.isGeneratingDescription
  );
  const setDescription = useCustomActivityStore(
    (state) => state.setDescription
  );
  const setIsGeneratingDescription = useCustomActivityStore(
    (state) => state.setIsGeneratingDescription
  );
  const currentStep = useCustomActivityStore((state) => state.currentStepIndex);
  const setCurrentStep = useCustomActivityStore(
    (state) => state.setCurrentStepIndex
  );
  const { handleNext } = useHandleNext();

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleNext(currentStep, setCurrentStep, questions.length);
    }
  };

  const generateDescription = async () => {
    if (!title) {
      toast({
        title: "Activity name required",
        description: "Please enter a name for your activity first",
        variant: "destructive",
      });
      return;
    }

    const { selectedLocation } = useTripStore.getState();
    if (!selectedLocation) {
      toast({
        title: "Location required",
        description: "Please select a location first",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingDescription(true);
    setDescription("");

    try {
      await AIService.getActivityDescription(
        selectedLocation.name,
        title,
        (chunk) => {
          setDescription((prev) => prev + chunk);
        },
        (error) => {
          console.error("Error generating description:", error);
          toast({
            title: "Failed to generate description",
            description: "Please try again or enter a description manually",
          });
        }
      );
    } catch (error) {
      console.error("Error generating description:", error);
      toast({
        title: "Failed to generate description",
        description: "Please try again or enter a description manually",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  return (
    <DescriptionStep
      value={description}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      generateDescription={generateDescription}
      isGenerating={isGeneratingDescription}
    />
  );
});

const DurationCostStepWrapper = React.memo(({ id }: { id: string }) => {
  const duration = useCustomActivityStore((state) => state.duration);
  const cost = useCustomActivityStore((state) => state.cost);
  const setDuration = useCustomActivityStore((state) => state.setDuration);
  const setCost = useCustomActivityStore((state) => state.setCost);
  const selectedCurrency = useCurrencyStore((state) => state.selectedCurrency);
  const currentStep = useCustomActivityStore((state) => state.currentStepIndex);
  const setCurrentStep = useCustomActivityStore(
    (state) => state.setCurrentStepIndex
  );
  const { handleNext } = useHandleNext();
  const currentQuestion = questions.find((q) => q.id === id);

  const value = id === "duration" ? duration : cost;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (id === "duration") {
      setDuration(e.target.value);
    } else if (id === "cost") {
      setCost(e.target.value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleNext(currentStep, setCurrentStep, questions.length);
    }
  };

  const prefix =
    id === "cost" ? selectedCurrency.symbol : currentQuestion?.prefix;

  return (
    <DurationCostStep
      id={id}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      prefix={prefix}
      suffix={currentQuestion?.suffix}
      placeholder={
        id === "duration" ? "e.g. 2" : id === "cost" ? "e.g. 25" : ""
      }
    />
  );
});

const TypesStepWrapper = React.memo(() => {
  const categories = useCustomActivityStore((state) => state.categories);
  const toggleCategory = useCustomActivityStore(
    (state) => state.toggleCategory
  );
  const currentStep = useCustomActivityStore((state) => state.currentStepIndex);
  const setCurrentStep = useCustomActivityStore(
    (state) => state.setCurrentStepIndex
  );
  const { handleNext } = useHandleNext();

  const onNextStep = useCallback(() => {
    handleNext(currentStep, setCurrentStep, questions.length);
  }, [currentStep, handleNext, setCurrentStep]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleNext(currentStep, setCurrentStep, questions.length);
      }
    },
    [currentStep, handleNext, setCurrentStep]
  );

  return (
    <TypesStep
      selectedTypes={categories}
      onTypeSelect={toggleCategory}
      onNext={onNextStep}
      onKeyDown={handleKeyDown}
    />
  );
});

const LocationStepWrapper = React.memo(() => {
  const location = useCustomActivityStore((state) => state.location);
  const title = useCustomActivityStore((state) => state.title);
  const setLocation = useCustomActivityStore((state) => state.setLocation);
  const currentStep = useCustomActivityStore((state) => state.currentStepIndex);
  const setCurrentStep = useCustomActivityStore(
    (state) => state.setCurrentStepIndex
  );
  const { handleNext } = useHandleNext();

  const handleChange = (newLocation: LocationInput) => {
    setLocation(newLocation);
  };

  const onNextStep = useCallback(
    (locationData?: LocationInput) => {
      handleNext(currentStep, setCurrentStep, questions.length, locationData);
    },
    [currentStep, handleNext, setCurrentStep]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleNext(currentStep, setCurrentStep, questions.length);
      }
    },
    [currentStep, handleNext, setCurrentStep]
  );

  return (
    <LocationStep
      value={location}
      onChange={handleChange}
      activityName={title}
      handleNext={onNextStep}
      onKeyDown={handleKeyDown}
    />
  );
});

const ImageStepWrapper = React.memo(() => {
  const { toast } = useToast();
  const images = useCustomActivityStore((state) => state.images);
  const uploadingImages = useCustomActivityStore(
    (state) => state.uploadingImages
  );
  const setUploadingImages = useCustomActivityStore(
    (state) => state.setUploadingImages
  );
  const addImage = useCustomActivityStore((state) => state.addImage);
  const removeImage = useCustomActivityStore((state) => state.removeImage);
  const currentStep = useCustomActivityStore((state) => state.currentStepIndex);
  const setCurrentStep = useCustomActivityStore(
    (state) => state.setCurrentStepIndex
  );
  const { handleNext } = useHandleNext();
  const [isDragging, setIsDragging] = useState(false);

  const validateFiles = (files: File[]): File[] => {
    const remainingSlots = 4 - images.length;
    if (remainingSlots <= 0) {
      toast({
        title: "Maximum images reached",
        description: "You can only upload up to 4 images",
        variant: "destructive",
      });
      return [];
    }

    const validFiles: File[] = [];
    const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB in bytes

    Array.from(files).forEach((file) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: `${file.name} is not a valid image type. Please use JPG, PNG, or WebP.`,
          variant: "destructive",
        });
        return;
      }

      if (file.size > MAX_SIZE) {
        toast({
          title: "File too large",
          description: `${file.name} exceeds 10MB limit.`,
          variant: "destructive",
        });
        return;
      }

      validFiles.push(file);
    });

    return validFiles.slice(0, remainingSlots);
  };

  const uploadFiles = async (filesToUpload: File[]) => {
    if (filesToUpload.length === 0) return;

    setUploadingImages(true);

    try {
      // First, upload the images and get URLs
      const uploadResponse = await experienceService.uploadCustomActivityImages(
        filesToUpload
      );
      const imageUrls = uploadResponse.data.urls;

      // Then update with files and URLs
      filesToUpload.forEach((file: File, index: number) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          addImage({
            file,
            preview: reader.result as string,
            url: imageUrls[index],
          });
        };
        reader.readAsDataURL(file);
      });
    } catch (error: unknown) {
      console.error("Error uploading images:", error);
      toast({
        title: "Upload failed",
        description:
          "There was an error uploading your images. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploadingImages(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const validFiles = validateFiles(Array.from(files));
    await uploadFiles(validFiles);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const validFiles = validateFiles(Array.from(files));
    await uploadFiles(validFiles);
  };

  const onNextStep = useCallback(() => {
    handleNext(currentStep, setCurrentStep, questions.length);
  }, [currentStep, handleNext, setCurrentStep]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleNext(currentStep, setCurrentStep, questions.length);
      }
    },
    [currentStep, handleNext, setCurrentStep]
  );

  return (
    <ImageStep
      images={images}
      uploadingImages={uploadingImages}
      handleImageUpload={handleImageUpload}
      removeImage={removeImage}
      onNext={onNextStep}
      onKeyDown={handleKeyDown}
      isDragging={isDragging}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    />
  );
});

// Memoized Preview component with optimized state selection
const Preview = React.memo(() => {
  // Use the useShallow hook to create a selector with shallow equality
  const formData = useCustomActivityStore(
    useShallow((state) => ({
      title: state.title,
      description: state.description,
      duration: state.duration,
      cost: state.cost,
      categories: state.categories,
      location: state.location,
      images: state.images,
    }))
  ) as FormData;

  const selectedCurrency = useCurrencyStore((state) => state.selectedCurrency);

  return (
    <div className="flex flex-col h-[35%]">
      <PreviewHeader />
      <div className="flex-1 border-neutral-200 border rounded-lg p-3 bg-white overflow-hidden min-h-0">
        <PreviewCard formData={formData} selectedCurrency={selectedCurrency} />
      </div>
    </div>
  );
});

const CustomActivityForm = () => {
  // const [currentStep, setCurrentStep] = useState(0);
  const currentStep = useCustomActivityStore((state) => state.currentStepIndex);
  const setCurrentStep = useCustomActivityStore(
    (state) => state.setCurrentStepIndex
  );
  const { handleNext } = useHandleNext();
  const setStoreCurrentStep = useCustomActivityStore(
    (state) => state.setCurrentStepIndex
  );

  // Sync the component state with the store state
  useEffect(() => {
    setStoreCurrentStep(currentStep);
  }, [currentStep, setStoreCurrentStep]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, setCurrentStep]);

  // Render current step based on step index
  const renderCurrentStep = useCallback(() => {
    const currentQuestion = questions[currentStep];
    if (!currentQuestion) return null;

    switch (currentQuestion.type.input) {
      case "input":
        if (currentQuestion.id === "title") {
          return <TitleStepWrapper />;
        } else {
          return <DurationCostStepWrapper id={currentQuestion.id} />;
        }

      case "textarea":
        return <DescriptionStepWrapper />;

      case "types":
        return <TypesStepWrapper />;

      case "location":
        return <LocationStepWrapper />;

      case "image":
        return <ImageStepWrapper />;

      default:
        return null;
    }
  }, [currentStep]);

  const onNextStep = useCallback(() => {
    handleNext(currentStep, setCurrentStep, questions.length);
  }, [currentStep, handleNext, setCurrentStep]);

  // Get selectedOption directly from the store in the rendering section
  const selectedOption = useCustomActivityStore(
    (state) => state.selectedOption
  );
  const setSelectedOption = useCustomActivityStore(
    (state) => state.setSelectedOption
  );
  const locationName = useTripStore((state) => state.selectedLocation?.name);

  return (
    <div className="h-[calc(100vh-7rem)] w-full rounded-md px-2 pb-2">
      <div className="h-full flex flex-col">
        {/* Main content area */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <div className="h-[calc(100%-3rem)] overflow-y-auto">
            <div className="min-h-full flex items-start px-1">
              <div className="w-full items-center justify-center">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="mt-2"
                  >
                    <div className="space-y-5">
                      <div className="space-y-1">
                        <QuestionTitle
                          question={questions[currentStep]?.question}
                        />
                      </div>

                      <div className="">{renderCurrentStep()}</div>

                      <div className="flex flex-col gap-3">
                        <NavigationButtons
                          currentStep={currentStep}
                          handleBack={handleBack}
                          handleNext={onNextStep}
                          isLastStep={currentStep === questions.length - 1}
                        />

                        {currentStep === questions.length - 1 && (
                          <SharingStep
                            selectedOption={selectedOption}
                            setSelectedOption={setSelectedOption}
                            locationName={locationName}
                          />
                        )}
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Preview section */}
        <Preview />
      </div>
    </div>
  );
};

export default CustomActivityForm;
